import { useEffect, useMemo, useState } from "react";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { dateUtility } from "../../../../../common/infrastructure/utilities/dateUtility";
import { PushDeliveryStatus } from "../../../../../common/enums";
import { IPaginatedResponse, MessageStatus } from "../../emailLogs/models/emailLogModel";
import { IPushLogModel } from "../models/pushLogModel";
import {
  getPushDeliveryStatusLabel,
  getPushDeviceLabel,
  getPushNotificationTypeLabel,
} from "../utils/pushLogUtility";

const PAGE_SIZE = 20;

export interface IPushLogDeviceRow {
  key: string;
  deviceLabel: string;
  status: PushDeliveryStatus;
  statusLabel: string;
  deliveredOn: string | null;
  openedOn: string | null;
}

export interface IPushLogRow {
  id: number;
  index: number;
  receiverName?: string;
  typeLabel: string;
  title: string;
  body: string;
  status: MessageStatus;
  /** As the API sends it (the studio's local time); the row formats it like the other logs. */
  createdOn: string;
  /** "09:29" and "понеделник, 12 октомври": the lock screen the notification is previewed on. */
  lockScreenTime: string;
  lockScreenDate: string;
  errorMessage?: string;
  devices: IPushLogDeviceRow[];
}

const pad = (value: number) => value.toString().padStart(2, "0");

const toRow = (item: IPushLogModel): IPushLogRow => {
  const sentAt = new Date(item.createdOn);

  return {
    id: item.id,
    index: item.index,
    receiverName: item.receiverName,
    typeLabel: getPushNotificationTypeLabel(item.type),
    title: item.title,
    body: item.body,
    status: item.status,
    createdOn: item.createdOn,
    lockScreenTime: `${pad(sentAt.getHours())}:${pad(sentAt.getMinutes())}`,
    lockScreenDate: sentAt.toLocaleDateString("bg-BG", {
      weekday: "long",
      day: "numeric",
      month: "long",
    }),
    errorMessage: item.errorMessage,
    devices: (item.deliveries ?? []).map((delivery, deliveryIndex) => ({
      key: `${item.id}-${deliveryIndex}`,
      deviceLabel: getPushDeviceLabel(delivery.userAgent),
      status: delivery.status,
      statusLabel: getPushDeliveryStatusLabel(delivery.status, delivery.failureReason),
      deliveredOn: delivery.deliveredOn ? dateUtility.formatDateTime(delivery.deliveredOn) : null,
      openedOn: delivery.openedOn ? dateUtility.formatDateTime(delivery.openedOn) : null,
    })),
  };
};

interface IUsePushLogsListParams {
  filterUserIds: number[] | null;
}

/**
 * The "Известия" tab of the communication logs: one row per notification; a click opens it on
 * a phone lock screen, with every device it went to and how far it got there.
 */
export const usePushLogsList = ({ filterUserIds }: IUsePushLogsListParams) => {
  const [page, setPage] = useState(1);
  const [selectedPush, setSelectedPush] = useState<IPushLogRow | null>(null);

  useEffect(() => {
    setPage(1);
  }, [filterUserIds]);

  const listParams = new URLSearchParams({ page: page.toString(), pageSize: PAGE_SIZE.toString() });
  filterUserIds?.forEach((id) => listParams.append("userIds", id.toString()));
  const listEndpoint = `${endpointsMetadata.communicationLogs.getPushLogs}?${listParams.toString()}`;

  const { data, isLoading } = useApiQuery<IPaginatedResponse<IPushLogModel>>({
    queryKey: [endpointsMetadata.communicationLogs.getPushLogs, page, filterUserIds],
    endpoint: listEndpoint,
    globalIsLoading: false,
  });

  const handlePageChange = (e: PaginatorPageChangeEvent) => {
    setPage(Math.floor(e.first / PAGE_SIZE) + 1);
  };

  const items = useMemo(() => (data?.items ?? []).map(toRow), [data]);
  const totalCount = data?.totalCount ?? 0;

  return {
    page,
    items,
    totalCount,
    isLoading,
    selectedPush,
    setSelectedPush,
    handlePageChange,
    PAGE_SIZE,
  };
};
